import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './dropdown-menu.js';
import type { SelectOption } from './select.js';
import { cn } from '../../lib/utils.js';

// Multi-value sibling of Select — same trigger and popover chrome, but
// items toggle instead of closing the menu, and the value is a list.
//
// An empty selection means "no restriction" and renders as `allLabel`
// ("All areas"), which is what a filter with nothing ticked should mean.
export function MultiSelect({
  options,
  value,
  onChange,
  allLabel = 'All',
  /** Noun for the summary line when several are picked, e.g. "areas". */
  itemNoun = 'selected',
  disabled,
  className,
  maxHeight = 320,
  searchable = true,
  searchPlaceholder = 'Search…',
}: {
  options: SelectOption<string>[];
  value: string[];
  onChange: (next: string[]) => void;
  allLabel?: string;
  itemNoun?: string;
  disabled?: boolean;
  className?: string;
  maxHeight?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const filtered = React.useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (v: string) => {
    onChange(
      value.includes(v) ? value.filter((x) => x !== v) : [...value, v],
    );
  };

  // One pick reads better as its own label than as "1 selected".
  const summary =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? value[0]!)
        : `${value.length} ${itemNoun}`;

  return (
    <DropdownMenu
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-3.5 py-2.5 text-left text-sm transition-colors',
            'hover:border-[hsl(var(--border-strong))] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background focus-visible:border-transparent',
            'data-[state=open]:border-[hsl(var(--border-strong))]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'dark:bg-card-raised dark:border-white/10',
            className,
          )}
        >
          <span className={cn('truncate', value.length === 0 && 'text-muted-foreground/70')}>
            {summary}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
        style={{ maxHeight, overflowY: 'auto' }}
      >
        {searchable && (
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[hsl(var(--hairline))] bg-popover px-2.5 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Suppress Radix typeahead while the search box has focus.
                if (e.key !== 'Escape' && e.key !== 'Tab') e.stopPropagation();
              }}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
            />
          </div>
        )}
        <div className="p-1">
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mb-1 flex w-full items-center rounded-sm px-2 py-1.5 text-left font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Clear selection
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-[13px] text-muted-foreground">
              No matches
            </div>
          ) : (
            filtered.map((opt) => {
              const active = value.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={opt.disabled}
                  // Plain buttons rather than DropdownMenuItem — Radix
                  // items close the menu on select, and picking several
                  // in a row is the whole point here.
                  onClick={() => toggle(opt.value)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[13px] transition-colors',
                    'hover:bg-muted focus:bg-muted focus:outline-none',
                    opt.disabled && 'pointer-events-none opacity-50',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border',
                    )}
                  >
                    {active && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1 truncate">{opt.label}</span>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
